import { useState, useEffect, useRef, useCallback } from "react";
import { useDispatch } from "react-redux";
import { debounce } from "lodash";
import {
  Box,
  TextField,
  ListItem,
  ListItemButton,
  Checkbox,
  ListItemText,
  Typography,
  CircularProgress,
} from "@mui/material";
import { useContext } from "react";
import { PlatformContext } from "./AddPromotions";
import { getPlatformCategory } from "../../redux/slices/promotions";
import {
  fetchAllListingItems,
  mergeListingSelection,
  removeListingSelectionByIds,
  isListingSelectAllChecked,
  shouldClearListingSelectAll,
  PROMO_SELECT_ALL_MAX,
} from "./promoListingSelectAll";

/**
 * CategoryList component fetches and displays a list of categories
 *
 * @param {Function} changeHandler - Callback function to handle category selection.
 * @param {Object} inputValue - Object/Array containing the currently selected category details.
 */
export default function CategoryList({
  changeHandler = () => {},
  inputValue = {},
  isShowAnyOption = false,
  listType = "",
  setAnchorEl,
}) {
  const dispatch = useDispatch();
  const [filter, setFilter] = useState({
    skip: 0,
    limit: 10,
    searchTerm: "",
  });
  const platform = useContext(PlatformContext);
  const [isLoading, setIsLoading] = useState(true);
  const [total, setTotal] = useState(0);

  const [categoriesList, setCategoriesList] = useState([]);
  const [selectAllActive, setSelectAllActive] = useState(false);
  const [selectAllLoading, setSelectAllLoading] = useState(false);
  const [selectAllScopeIds, setSelectAllScopeIds] = useState([]);

  const observer = useRef();

  const mapCategoryItem = (item) => ({
    categoryId: item?.categoryId,
    categoryName: item?.categoryName,
    categorySlug: item?.categorySlug,
    status: item?.status,
  });

  const lastPostElementRef = useCallback(
    (node) => {
      if (isLoading || categoriesList.length >= total) return;
      if (observer.current) observer.current.disconnect();
      const { skip, limit, searchTerm } = filter;

      observer.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            const newSkip = skip + limit;
            setIsLoading(true);

            dispatch(
              getPlatformCategory({
                skip: newSkip,
                limit,
                platform: platform,
                searchTerm: searchTerm || "",
              })
            )
              .unwrap()
              .then((res) => {
                const { data, total: newTotal } = res;
                const newCategories = data?.map(mapCategoryItem);
                setCategoriesList((prev) => [...prev, ...newCategories]);
                setTotal(newTotal);
                setFilter((prev) => ({
                  ...prev,
                  skip: newSkip,
                }));
              })
              .finally(() => setIsLoading(false));
          }
        },
        {
          root: null,
          rootMargin: "100px",
          threshold: 0.1,
        }
      );

      if (node) observer.current.observe(node);
    },
    [isLoading, categoriesList.length, total, filter, platform, dispatch]
  );

  useEffect(() => {
    setIsLoading(true);
    dispatch(
      getPlatformCategory({
        skip: filter?.skip,
        limit: filter?.limit,
        platform: platform,
        searchTerm: filter?.searchTerm || "",
      })
    )
      .unwrap()
      .then((res) => {
        setIsLoading(false);

        const { data, total } = res;
        const categoryData = data?.map(mapCategoryItem);
        setTotal(total);
        setCategoriesList(categoryData);
        setSelectAllActive(false);
        setSelectAllScopeIds([]);
      })
      .catch(() => {
        setIsLoading(false);
      });
  }, [platform]);

  const listingSelectAllParams = {
    platform,
    searchTerm: filter.searchTerm || "",
  };

  const isAllSelected = isListingSelectAllChecked({
    inputValue,
    visibleItems: categoriesList,
    idKey: "categoryId",
    scopeIds: selectAllScopeIds,
    selectAllActive,
    total,
  });

  const handleSelectAll = async () => {
    if (!Array.isArray(inputValue)) return;

    if (
      shouldClearListingSelectAll({
        inputValue,
        visibleItems: categoriesList,
        idKey: "categoryId",
        scopeIds: selectAllScopeIds,
        selectAllActive,
        total,
      })
    ) {
      setSelectAllLoading(true);
      try {
        let idsToRemove = selectAllScopeIds;
        if (!idsToRemove.length) {
          const { items } = await fetchAllListingItems({
            dispatch,
            fetchThunk: getPlatformCategory,
            params: listingSelectAllParams,
            mapItem: mapCategoryItem,
            pageSize: filter.limit,
          });
          idsToRemove = items.map((c) => c.categoryId).filter(Boolean);
        }
        changeHandler({
          ruleValue: removeListingSelectionByIds(inputValue, idsToRemove, "categoryId"),
        });
        setSelectAllActive(false);
        setSelectAllScopeIds([]);
      } catch {
        // keep prior selection on failure
      } finally {
        setSelectAllLoading(false);
      }
      return;
    }

    setSelectAllLoading(true);
    try {
      const { items, total: apiTotal, capped } = await fetchAllListingItems({
        dispatch,
        fetchThunk: getPlatformCategory,
        params: listingSelectAllParams,
        mapItem: mapCategoryItem,
        pageSize: filter.limit,
      });

      const ids = items.map((c) => c.categoryId).filter(Boolean);
      setSelectAllScopeIds(ids);
      changeHandler({
        ruleValue: mergeListingSelection(inputValue, items, "categoryId"),
      });
      setSelectAllActive(true);
      if (capped && typeof console !== "undefined") {
        console.warn(
          `Select all capped at ${PROMO_SELECT_ALL_MAX} of ${apiTotal} categories. Narrow your search if needed.`
        );
      }
    } catch {
      // keep prior selection on failure
    } finally {
      setSelectAllLoading(false);
    }
  };


  const Row = ({ index, style }) => {
    const category = categoriesList[index];

    const isChecked = Array.isArray(inputValue)
      ? inputValue.some((item) => item?.categoryId === category?.categoryId)
      : inputValue?.categoryId === category?.categoryId;

    return (
      <ListItem
        key={category?.categoryId}
        disablePadding
        style={style}
        ref={categoriesList.length === index + 1 ? lastPostElementRef : null}
      >
        <ListItemButton
          onClick={() => {
            if (Array.isArray(inputValue)) {
              if (!isChecked) {
                changeHandler({ ruleValue: [...inputValue, category] });
              } else {
                changeHandler({
                  ruleValue: inputValue.filter((item) => item?.categoryId !== category?.categoryId),
                });
              }
            } else {
              setAnchorEl(null);
              changeHandler({
                ruleValue: isChecked ? inputValue : category,
              });
            }
          }}
          sx={{
            p: 0,
            bgcolor: (_theme) => (isChecked ? "action.selected" : "inherit"),
          }}
          className="popover-btn-area bg-transparent"
        >
          {(Array.isArray(inputValue)) && (
            <Checkbox
              edge="start"
              checked={isChecked}
              tabIndex={-1}
              disableRipple
              onClick={(e) => {
                e.stopPropagation();
                if (e.target.checked) {
                  if (!isChecked) {
                    changeHandler({ ruleValue: [...inputValue, category] });
                  }
                } else {
                  changeHandler({
                    ruleValue: inputValue.filter((item) => item?.categoryId !== category?.categoryId),
                  });
                }
              }}
              style={{ margin: 0 }}
            />
          )}

          <ListItemText
            primary={
                <Typography variant="body2" color="#2E2E3A" fontWeight="semibold">
                  {category.categoryName}
                </Typography>
            }
          />
        </ListItemButton>
      </ListItem>
    );
  };

  return (
    <Box
      sx={{
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        overflow: "hidden",
      }}
    >
      {/* Search Box */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search categories..."
          onChange={debounce((e) => {
            const searchValue = e.target.value;
            setIsLoading(true);
            setFilter((prev) => ({ ...prev, searchTerm: searchValue, skip: 0 }));

            dispatch(
              getPlatformCategory({
                skip: 0,
                limit: filter?.limit,
                platform: platform,
                searchTerm: searchValue || "",
              })
            )
              .unwrap()
              .then((res) => {
                setIsLoading(false);
                const { data, total } = res;
                const categoryData = data?.map(mapCategoryItem);
                setTotal(total);
                setCategoriesList(categoryData);
                setSelectAllActive(false);
                setSelectAllScopeIds([]);
              })
              .catch(() => {
                setIsLoading(false);
              });
          }, 500)}
        />
      </Box>

     
      {/* Empty State */}
      {!isLoading && categoriesList?.length === 0 && (
        <Box sx={{ p: 3, transform: "scale(0.9)" }}>
          Data Not Found
        </Box>
      )}
      {Array.isArray(inputValue) && categoriesList?.length > 0 && (
        <ListItem
          disablePadding
          sx={{
            borderBottom: "1px solid",
            borderColor: "divider",
            position: "sticky",
            top: 0,
            bgcolor: "background.paper",
            zIndex: 1,
          }}
        >
          <ListItemButton onClick={handleSelectAll} className="p-0" disabled={selectAllLoading}>
            <Checkbox
              checked={isAllSelected}
              indeterminate={
                !selectAllLoading &&
                Array.isArray(inputValue) &&
                inputValue.length > 0 &&
                !isAllSelected
              }
              disabled={selectAllLoading}
              tabIndex={-1}
              disableRipple
              onClick={(e) => {
                e.stopPropagation();
                handleSelectAll();
              }}
            />
            <ListItemText
              primary={
                <Typography fontWeight={600}>
                  {selectAllLoading
                    ? "Selecting all…"
                    : total > 0
                      ? `Select all (${total})`
                      : "Select all"}
                </Typography>
              }
              secondary={
                total > PROMO_SELECT_ALL_MAX ? (
                  <Typography variant="caption" color="text.secondary">
                    Max {PROMO_SELECT_ALL_MAX} per rule
                  </Typography>
                ) : null
              }
            />
          </ListItemButton>
        </ListItem>
      )}

      <Box sx={{ maxHeight: 300, overflow: "auto" }}>
        {categoriesList.map((_, index) => (
          <Row key={categoriesList[index]?.categoryId} index={index} />
        ))}
      </Box>
       {/* Loading State */}
      <>
        {isLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
            <CircularProgress size={24} color="primary" />
          </Box>
        )}{" "}
      </>
    </Box>
  );
}
